import { AvailableModel, ThinkingModeEffortLevel } from "../../../../services/claude";
import { ArtifactType } from "../../../../types/artifact";
import {
  getSavedAnswerMode,
  getSavedArtifactTypes,
  getSavedBudgetTokens,
  getSavedEffort,
  getSavedMaxInputTokens,
  getSavedMaxTokens,
  getSavedModel,
  getSavedTemperature,
  getSavedThinkingMode,
  saveAnswerMode,
  saveArtifactTypes,
  saveBudgetTokens,
  saveEffort,
  saveMaxInputTokens,
  saveMaxTokens,
  saveModel,
  saveTemperature,
  saveThinkingMode
} from "../../utils/ragchatStorage";
import { AnswerMode, ChatActions, ChatGetState, ChatSetState, ChatState, DATASET_PRESET_TYPES, DatasetPreset } from "../chatTypes";

type UserPreferencesActions = Pick<
  ChatActions,
  | "setTemperature"
  | "setThinkingMode"
  | "setMaxTokens"
  | "setBudgetTokens"
  | "setMaxInputTokens"
  | "setModel"
  | "setEffort"
  | "setIncludedArtifactTypes"
  | "setAnswerMode"
  | "setDatasetPreset"
  | "loadPreferencesFromStorage"
>;

const THINKING_MODE_DEFAULT = true;

const persistWhenEnabled = (get: ChatGetState, saveFn: () => void): void => {
  if (get().thinkingModeEnabled) {
    saveFn();
  }
};

const loadStoredPreferences = (): Partial<ChatState> => ({
  temperature: getSavedTemperature(),
  thinkingMode: getSavedThinkingMode(THINKING_MODE_DEFAULT),
  maxTokens: getSavedMaxTokens(),
  budgetTokens: getSavedBudgetTokens(),
  maxInputTokens: getSavedMaxInputTokens(),
  model: getSavedModel(),
  effort: getSavedEffort(),
  answerMode: getSavedAnswerMode(),
  includedArtifactTypes: getSavedArtifactTypes()
});

export const createUserPreferencesActions = (set: ChatSetState, get: ChatGetState): UserPreferencesActions => ({
  setTemperature: (value: number): void => {
    set({ temperature: value });
    persistWhenEnabled(get, () => saveTemperature(value));
  },

  setThinkingMode: (value: boolean): void => {
    set({ thinkingMode: value });
    persistWhenEnabled(get, () => saveThinkingMode(value));
  },

  setMaxTokens: (value: number): void => {
    set({ maxTokens: value });
    persistWhenEnabled(get, () => saveMaxTokens(value));
  },

  setBudgetTokens: (value: number): void => {
    set({ budgetTokens: value });
    persistWhenEnabled(get, () => saveBudgetTokens(value));
  },

  setMaxInputTokens: (value: number): void => {
    set({ maxInputTokens: value });
    persistWhenEnabled(get, () => saveMaxInputTokens(value));
  },

  setModel: (value: AvailableModel): void => {
    set({ model: value });
    persistWhenEnabled(get, () => saveModel(value));
  },

  setEffort: (value: ThinkingModeEffortLevel): void => {
    set({ effort: value });
    persistWhenEnabled(get, () => saveEffort(value));
  },

  setIncludedArtifactTypes: (types: ArtifactType[]): void => {
    set({ includedArtifactTypes: types });
    persistWhenEnabled(get, () => saveArtifactTypes(types));
  },

  setAnswerMode: (mode: AnswerMode): void => {
    set({ answerMode: mode });
    persistWhenEnabled(get, () => saveAnswerMode(mode));
  },

  setDatasetPreset: (preset: DatasetPreset): void => {
    const types = DATASET_PRESET_TYPES[preset];
    set({ includedArtifactTypes: types });
    persistWhenEnabled(get, () => saveArtifactTypes(types));
  },

  loadPreferencesFromStorage: (): void => {
    set(loadStoredPreferences());
  }
});
